/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Entity,
  getEntitySourceLocation,
  parseLocationRef,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  ANNOTATION_SOURCE_LOCATION,
} from '@backstage/catalog-model';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { JsonArray } from '@backstage/types';
import { Chip, Grid, makeStyles } from '@material-ui/core';
import { MarkdownContent, Progress } from '@backstage/core-components';
import React from 'react';
import useAsync from 'react-use/lib/useAsync';
import { AboutField } from './AboutField';
import { LinksGridList } from '../EntityLinksCard/LinksGridList';
import { useGithubApi } from '../../../hooks/useGithubApi';
// import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles({
  description: {
    wordBreak: 'break-word',
  },
  issueButton: {
    marginTop: '16px',
    padding: '4px 10px',
  },
  issueButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
});

/**
 * Props for {@link AboutContent}.
 *
 * @public
 */
export interface AboutContentProps {
  entity: Entity;
}

function getLocationTargetHref(
  target: string,
  type: string,
  entitySourceLocation: {
    type: string;
    target: string;
  },
): string {
  if (type === 'url' || target.includes('://')) {
    return target;
  }

  const srcLocationUrl =
    entitySourceLocation.type === 'file'
      ? `file://${entitySourceLocation.target}`
      : entitySourceLocation.target;

  if (type === 'file' || entitySourceLocation.type === 'file') {
    return new URL(target, srcLocationUrl).href;
  }

  return srcLocationUrl;
}

/** @public */
export function AboutContent(props: AboutContentProps) {
  const { entity } = props;
  const classes = useStyles();
  const fetchGithubApi = useGithubApi();
  const isSystem = entity.kind.toLocaleLowerCase('en-US') === 'system';
  const isResource = entity.kind.toLocaleLowerCase('en-US') === 'resource';
  const isComponent = entity.kind.toLocaleLowerCase('en-US') === 'component';
  const isAPI = entity.kind.toLocaleLowerCase('en-US') === 'api';
  const isTemplate = entity.kind.toLocaleLowerCase('en-US') === 'template';
  const isLocation = entity.kind.toLocaleLowerCase('en-US') === 'location';
  const isGroup = entity.kind.toLocaleLowerCase('en-US') === 'group';

  let entityMetadataSourceLocation: string | undefined;
  let pathname: string | undefined;

  if (entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION]) {
    entityMetadataSourceLocation =
      entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION];
    const entitySourceLocation = parseLocationRef(entityMetadataSourceLocation);

    if (entitySourceLocation.type === 'url') {
      const url = new URL(entitySourceLocation.target);
      const originalPathname = url.pathname;

      pathname = originalPathname.endsWith('/')
        ? originalPathname.slice(0, -1)
        : originalPathname;
    }
  }

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    {
      kind: 'component',
    },
  );
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  let entitySourceLocation:
    | {
        type: string;
        target: string;
      }
    | undefined;
  try {
    entitySourceLocation = getEntitySourceLocation(entity);
  } catch (e) {
    entitySourceLocation = undefined;
  }

  const {
    value: repo,
    loading,
    error: githubError,
  } = useAsync(async () => {
    if (!pathname) {
      return undefined;
    }

    return fetchGithubApi(pathname);
  }, []);

  if (loading) {
    return <Progress />;
  }

  let repoStatsComponent = null;

  if (githubError) {
    /*     const githubIssueLink =
    'https://github.com/newscorp-ghfb/global-backstage/issues/new?assignees=&labels=&projects=&template=bug_report.md&title=Github integration error';
    repoStatsComponent = (
      <Alert severity="error">
        There was an error in the Github integration: {githubError?.message}
        <span className={classes.issueButtonContainer}>
          <LinkButton className={classes.issueButton} to={githubIssueLink} color="secondary" variant="outlined">
            Report this error
          </LinkButton>
        </span>
      </Alert>
    ); */
    repoStatsComponent = null;
    // eslint-disable-next-line no-console
    console.error(githubError);
  } else {
    repoStatsComponent = !!entityMetadataSourceLocation && (
      <>
        <AboutField
          label="Stargazer Count"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={`${repo?.stargazers}`}
        />

        <AboutField
          label="Days Since Last Commit"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={`${repo?.daysSinceLastCommit}`}
        />

        <AboutField
          label="Days Since Last Release"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={
            repo?.daysSinceLastRelease !== undefined &&
            repo?.daysSinceLastRelease !== null
              ? repo.daysSinceLastRelease.toString()
              : 'No Releases'
          }
        />

        <AboutField
          label="Watchers Count"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={`${repo?.subscribers}`}
        />

        <AboutField
          label="Open Pull Requests"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={`${repo?.openPullRequests}`}
        />
      </>
    );
  }

  return (
    <Grid container>
      <AboutField label="Description" gridSizes={{ xs: 12 }}>
        <MarkdownContent
          className={classes.description}
          content={entity?.metadata?.description || 'No description'}
        />
      </AboutField>
      <AboutField
        label="Owner"
        value="No Owner"
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {ownedByRelations.length > 0 && (
          <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
        )}
      </AboutField>
      {(isSystem || partOfDomainRelations.length > 0) && (
        <AboutField
          label="Domain"
          value="No Domain"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfDomainRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfDomainRelations}
              defaultKind="domain"
            />
          )}
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        partOfSystemRelations.length > 0) && (
        <AboutField
          label="System"
          value="No System"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfSystemRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfSystemRelations}
              defaultKind="system"
            />
          )}
        </AboutField>
      )}
      {isComponent && partOfComponentRelations.length > 0 && (
        <AboutField
          label="Parent Component"
          value="No Parent Component"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <EntityRefLinks
            entityRefs={partOfComponentRelations}
            defaultKind="component"
          />
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        isTemplate ||
        isGroup ||
        isLocation ||
        typeof entity?.spec?.type === 'string') && (
        <AboutField
          label="Type"
          value={entity?.spec?.type as string}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
      {(isAPI ||
        isComponent ||
        typeof entity?.spec?.lifecycle === 'string') && (
        <AboutField
          label="Lifecycle"
          value={entity?.spec?.lifecycle as string}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
      <AboutField
        label="Tags"
        value="No Tags"
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {(entity?.metadata?.tags || []).map(t => (
          <Chip key={t} size="small" label={t} />
        ))}
      </AboutField>
      {isLocation && (entity?.spec?.targets || entity?.spec?.target) && (
        <AboutField label="Targets" gridSizes={{ xs: 12 }}>
          <LinksGridList
            cols={1}
            items={((entity.spec.targets as JsonArray) || [entity.spec.target])
              .map(target => target as string)
              .map(target => ({
                text: target,
                href: getLocationTargetHref(
                  target,
                  (entity?.spec?.type || 'unknown') as string,
                  entitySourceLocation!,
                ),
              }))}
          />
        </AboutField>
      )}
      {repoStatsComponent}
    </Grid>
  );
}
